"use client";

/**
 * AuthGate — wraps all app routes and ensures only authenticated users
 * can proceed. Unauthenticated visitors are redirected to /signin with a
 * `redirect` query param so they land back on the right page after login.
 *
 * Public routes that bypass the gate (auth:false items in lib/menu-config come in automatically):
 *   /signin, /login, /onboarding, /embed/*
 *   /home, /dashboard, /charts, /search ← sidebar auth:false + public search
 *   /artists/*, /music/*, /news/*       ← publicly viewable detail pages
 */

import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { BBLogoLoader } from '@/components/loader/bb-logo-loader';
// Single source of truth: every sidebar item marked auth:false is, by
// definition, viewable by visitors — pull those hrefs in so this gate can never
// drift out of sync with the menu (the "Why BB → sign-in" bug).
import { PUBLIC_NAV_HREFS } from '@/components/layout/sidebar-nav';

// Exact-match only (these would swallow all routes if used as prefixes)
// /platform = public transparency page; /supporters = public supporter wall.
// /start = the public landing/welcome page (front door for logged-out
// visitors) — not in the menu so PUBLIC_NAV_HREFS never covers it.
// /help is public (Gordon 2026-11-02: visitors need the coπ tutorial); /academy stays members-only.
// /launch = the pro-launch pitch (keynote 13 Nov 2026) — shared with the room via QR.
// /dj-lab = test bench for the new DJ deck engine (not in the menu; plays public tracks only).
const PUBLIC_EXACT = ['/epx', '/', '/help', '/dashboard', '/platform', '/supporters', '/start', '/osx', '/codaw', '/lyriadj', '/dj-lyria', '/launch', '/ios', '/android', '/dj-lab', '/screens', '/survey'];

// Prefix-match: pathname must equal or start with these
const PUBLIC_PREFIXES = [
  '/signin',
  '/login',
  '/onboarding',
  '/embed/',
  // Standalone artist microsites (own domain / subdomain) — fully public
  '/site/',
  '/site',
  // Universal search is public — visitors can search without signing in.
  '/search',
  // Publicly viewable detail pages (SEO-important, linked from home/artists/news)
  '/artists/',
  '/music/',
  // '/news' (no trailing slash) covers the LISTING page too — it used to
  // bounce visitors to /signin while article pages were public (Gordon:
  // "make news visible for visitors").
  '/news',
  // Charts are public (linked from the home filter bar for everyone).
  '/charts',
  // The tuner is a free tool for everyone (coπ: "open tuner").
  '/studio/tuner',
  // Chords & scales explorer — coπ's "show me a diminished chord" for everyone.
  '/academy/chords-and-scales',
  '/academy/note-reading',
  '/academy/grooves',
  '/academy/level',
  // Nobody owns BB — anyone can ask to join the board or team (Gordon 2026-11-02).
  '/join',
  // EP-X runs signed out too (its own app); BB just hosts it in Studio.
  '/studio/epx',
  // Home section pages whose data is public (Gordon 2026-10-31: a section
  // title opens its page — visitors see these sections, so the page too).
  '/releases',
  '/artists',
  '/videos',
  '/labels',
  '/playlists',
  // A PUBLIC playlist opens for visitors too (its page hides the others).
  '/playlist/',
  '/demos',
  // Live broadcast viewer — share links must work for people without accounts.
  '/live/',
  // Booking payment links — clients paying a talent are usually not signed in.
  '/book/pay/',
  // Sync licensing catalog — buyers (music supervisors, brands) are EXTERNAL
  // people; shared ?track= links must open and play without an account
  // (Gordon 2026-09-11: "even when not logged in").
  '/synchronization',
  // Every sidebar item marked auth:false (/home, /magazines, /why-bb,
  // /platform, /supporters, /dashboard, …) — kept in sync automatically.
  ...PUBLIC_NAV_HREFS,
];

function isPublicPath(pathname: string) {
  if (PUBLIC_EXACT.includes(pathname)) return true;
  return PUBLIC_PREFIXES.some(
    p => pathname === p || pathname.startsWith(p)
  );
}

export function AuthGate({ children }: PropsWithChildren) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  const publicPath = isPublicPath(pathname);

  useEffect(() => {
    if (loading) return;
    if (!user && !publicPath) {
      // Keep the query too — /people?q=guitar comes back filtered after sign-in.
      router.replace(`/signin?redirect=${encodeURIComponent(pathname + (typeof window !== 'undefined' ? window.location.search : ''))}`);
    }
  }, [user, loading, publicPath, pathname, router]);

  // Public routes never need gating, so their content should paint
  // immediately regardless of auth-loading state — blocking on Firebase
  // auth resolving here used to render a blank white screen (no content,
  // no loader) for however long auth took to resolve on first load.
  if (publicPath) return <>{children}</>;

  // Private routes: show the BB logo loader while auth resolves (replaces
  // the bare-body white flash), and keep it up during the brief window
  // before the redirect effect above kicks in.
  if (loading) return <BBLogoLoader />;
  if (!user) return <BBLogoLoader />;

  return <>{children}</>;
}
